import React, { useState, useEffect, useContext } from 'react';
import './UpdatedCorporationDetail.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UpdateRow from './UpdateRow';
import WebLinkList from './WebLinkList';
import convenienceApi from '../../common/api/convenience';
import webLinksAreDifferent from '../../common/webLinksAreDifferent';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import logComponent from '../../common/logComponent';

export default function UpdatedCorporationDetail({ corporation, linkToFacility }) {
  const environmentContext = useContext(EnvironmentContext);
  const [prevCorporationWeblinks, setPrevCorporationWeblinks] = useState(null);
  const [prevCorporationWeblinksError, setPrevCorporationWeblinksError] = useState(false);
  const [prevCorporationWeblinksLoading, setPrevCorporationWeblinksLoading] = useState(false);
  const { hasToken } = environmentContext;
  const { updatedFields, relatedMimiCorporation: prevCorporation } = corporation.uiData;

  if (logComponent('UpdatedCorporationDetail')) { console.debug('UpdatedCorporationDetail rendered'); }

  useEffect(() => {
    if (logComponent('UpdatedCorporationDetail')) { console.debug('UpdatedCorporationDetail onMount'); }
    if (!hasToken) return;

  }, [])

  useEffect(() => {
    if (logComponent('UpdatedCorporationDetail')) { console.debug('UpdatedCorporationDetail useEffect [environmentContext.hasToken]'); }
    if (!hasToken) return;

    loadPrevCorporationWeblinks();
  }, [environmentContext.hasToken])

  const loadPrevCorporationWeblinks = () => {
    if (logComponent('UpdatedCorporationDetail')) { console.debug('UpdatedCorporationDetail loadPrevCorporationWeblinks'); }
    if (!hasToken) return;
    const { relatedMimiCorporation } = corporation.uiData;

    setPrevCorporationWeblinks(null);
    setPrevCorporationWeblinksError(false);
    setPrevCorporationWeblinksLoading(true);


    convenienceApi.getWeblinksForOrganization(relatedMimiCorporation)
      .then(
        result => {

          setPrevCorporationWeblinks(result);
          setPrevCorporationWeblinksError(false);
          setPrevCorporationWeblinksLoading(false);
        },
        error => {
          setPrevCorporationWeblinks(null);
          setPrevCorporationWeblinksError(true);
          setPrevCorporationWeblinksLoading(false);
        }
      );
  }

  const handleFacilityClick = (e) => {
    if (logComponent('UpdatedCorporationDetail')) { console.debug('UpdatedCorporationDetail handleFacilityClick'); }
    const { facilityId } = e.currentTarget.dataset;

    // get facility based on id
    // NOTE: we want == in this case b/c the facilityId coming from the
    // dataset is a string, but the value in the linkedFacility is a number
    const targetFacility = corporation.uiData.linkedFacilities.find(facility => 
      facility.salesforceAccountId &&
      facility.salesforceAccountId == facilityId); // eslint-disable-line

    linkToFacility(targetFacility);
  }

  return (
    <div className="Updated-Corporation-Detail">
      <h4 className="Updated-Corporation-Detail__heading">{corporation.name}</h4>
      <div className="Updated-Corporation-Detail__row">
        <p className="Updated-Corporation-Detail__instructions">Please accept or reject this updated corporation.</p>
        <p className="Updated-Corporation-Detail__legend">
          <FontAwesomeIcon
            className="Updated-Corporation-Detail__legend-icon"
            icon="sync-alt"></FontAwesomeIcon>
          indicates updated values
        </p>
      </div>
      <h5 className="Updated-Corporation-Detail__row">
        <span className="Updated-Corporation-Detail__sub-heading">Current values</span>
        <span className="Updated-Corporation-Detail__sub-heading">New values</span>
      </h5>
      <ul className="Updated-Corporation-Detail__list">
        <UpdateRow
          fieldName="Corporation Name"
          currentValue={prevCorporation.name}
          newValue={corporation.name}
          isUpdated={updatedFields.includes('name')} />
        <UpdateRow
          fieldName="About Us"
          currentValue={prevCorporation.aboutUs}
          newValue={corporation.aboutUs}
          isUpdated={updatedFields.includes('aboutUs')} />
        <UpdateRow
          fieldName="Postal Code"
          currentValue={prevCorporation.postalCode}
          newValue={corporation.postalCode}
          isUpdated={updatedFields.includes('postalCode')} />
        <UpdateRow
          fieldName="Industry Type"
          currentValue={prevCorporation.industryType}
          newValue={corporation.industryType}
          isUpdated={updatedFields.includes('industryType')} />
        <UpdateRow
          fieldName="Salesforce Account Id"
          currentValue={prevCorporation.salesforceAccountId}
          newValue={corporation.salesforceAccountId}
          isUpdated={updatedFields.includes('salesforceAccountId')} />
        <UpdateRow
          fieldName="Web Links"
          currentValue={prevCorporationWeblinks && prevCorporationWeblinks.length > 0 ? <WebLinkList links={prevCorporationWeblinks} /> : null}
          newValue={corporation.webLinks.length > 0 ? <WebLinkList links={corporation.webLinks} /> : null}
          isUpdated={webLinksAreDifferent(prevCorporationWeblinks, corporation.webLinks)}
          isLoading={prevCorporationWeblinksLoading}
          isError={prevCorporationWeblinksError}
          reloadAction={loadPrevCorporationWeblinks} />
        <li key="corporationDetailList" className="Updated-Corporation-Detail__list-row">
          <span className="Updated-Corporation-Detail__field-name-sm">Linked Facilities
            {
              updatedFields.includes('linkedFacilities') &&
              <FontAwesomeIcon className="Updated-Corporation-Detail__legend-icon" icon="sync-alt" />
            }
          </span>
          <div className="Updated-Corporation-Detail__item">
            <span className="Updated-Corporation-Detail__field-source">CURRENT</span>
            <span className="Updated-Corporation-Detail__field-name">Linked Facilities</span>
            <span className="Updated-Corporation-Detail__field-value">

              {
                prevCorporation.uiData.linkedFacilities.length === 0 ?
                  <span className="Updated-Corporation-Detail__empty-field">Not Provided</span> :
                  <ul className="Updated-Corporation-Detail__facility-list">
                    {
                      prevCorporation.uiData.linkedFacilities.map(facility =>
                        <li
                          className="Updated-Corporation-Detail__facility-item"
                          key={facility.salesforceSiteId} >
                          <span className="Updated-Corporation-Detail__facility-id">{facility.salesforceSiteId}</span>
                          <span className="Updated-Corporation-Detail__facility-name">
                            {facility.name}
                            {
                              (!facility.status || facility.status !== 'rejected') &&
                              <button
                                className="Updated-Corporation-Detail__facility-link"
                                data-facility-id={facility.salesforceAccountId}
                                onClick={handleFacilityClick}>
                                <FontAwesomeIcon icon="external-link-alt" />
                              </button>
                            }
                          </span>
                        </li>
                      )
                    }
                  </ul>
              }

            </span>
          </div>
          <div className="Updated-Corporation-Detail__item">
            <span className="Updated-Corporation-Detail__field-source">NEW</span>
            <span className="Updated-Corporation-Detail__field-name">Linked Facilities
              {
                updatedFields.includes('linkedFacilities') &&
                <FontAwesomeIcon className="Updated-Corporation-Detail__legend-icon" icon="sync-alt" />
              }
            </span>
            <span className="Updated-Corporation-Detail__field-value">

              {
                corporation.uiData.linkedFacilities.length === 0 ?
                  <span className="Updated-Corporation-Detail__empty-field">Not Provided</span> :
                  <ul className="Updated-Corporation-Detail__facility-list">
                    {
                      corporation.uiData.linkedFacilities.map(facility =>
                        <li
                          className="Updated-Corporation-Detail__facility-item"
                          key={facility.salesforceSiteId}>
                          <span className="Updated-Corporation-Detail__facility-id">{facility.salesforceSiteId}</span>
                          <span className="Updated-Corporation-Detail__facility-name">
                            {facility.name}
                            {
                              (!facility.status || facility.status !== 'rejected') &&
                              <button
                                className="Updated-Corporation-Detail__facility-link"
                                data-facility-id={facility.salesforceAccountId}
                                onClick={handleFacilityClick}>
                                <FontAwesomeIcon icon="external-link-alt" />
                              </button>
                            }
                          </span>
                        </li>
                      )
                    }
                  </ul>
              }
            </span>
          </div>
        </li>
      </ul>
    </div>


  );
}